import React, { useState, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../ui/dialog';
import { DollarSign, Package, Truck, Hash, Calendar, MessageSquare, Box } from 'lucide-react';
import OfferSection from './OfferSection';

const DEFAULT_FORM = {
  purchase_price: '', supplier_reference: '', availability_status: 'unknown',
  date_disponibilite_iso: '', commentaire_disponibilite: '',
  minimum_commande: '', maximum_commande: '', multiple_commande: '', colisage: '',
  promotional_mechanics: [],
  dimension_carton: '', nombre_carton_par_palette: ''
};

/**
 * DistributionAttachForm V4 — promotional_mechanics as source of truth
 */
const DistributionAttachForm = ({ open, onClose, product, onSubmit, submitting = false, initialData = null, editMode = false }) => {
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const offerRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      if (initialData) {
        const f = { ...DEFAULT_FORM };
        for (const k of Object.keys(f)) {
          if (k === 'promotional_mechanics') {
            f[k] = Array.isArray(initialData[k]) ? initialData[k] : [];
            continue;
          }
          const v = initialData[k];
          if (k.includes('_iso')) f[k] = (v || '').slice(0, 10);
          else if (v != null) f[k] = String(v);
        }
        setForm(f);
      } else {
        setForm({ ...DEFAULT_FORM });
      }
    }
  }, [initialData, open]);

  const set = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!product?.id) return;
    // Validate mechanics before submit
    if (form.promotional_mechanics?.length > 0 && offerRef.current?.validate) {
      if (!offerRef.current.validate()) return;
    }
    const num = (v) => v !== '' ? parseFloat(v) : null;
    const int = (v) => v !== '' ? parseInt(v) : null;
    const str = (v) => v || null;

    onSubmit(product.id, {
      product_id: product.id,
      purchase_price: num(form.purchase_price),
      supplier_reference: str(form.supplier_reference),
      availability_status: form.availability_status || 'unknown',
      date_disponibilite_iso: str(form.date_disponibilite_iso),
      commentaire_disponibilite: str(form.commentaire_disponibilite),
      minimum_commande: int(form.minimum_commande),
      maximum_commande: int(form.maximum_commande),
      multiple_commande: int(form.multiple_commande),
      colisage: int(form.colisage),
      promotional_mechanics: form.promotional_mechanics || [],
      dimension_carton: str(form.dimension_carton),
      nombre_carton_par_palette: int(form.nombre_carton_par_palette)
    });
  }, [product, form, onSubmit]);

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="distribution-attach-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">{editMode ? 'Modifier ma distribution' : 'Créer ma distribution'}</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {editMode ? 'Mettre à jour votre offre commerciale' : 'Rattacher votre offre commerciale à ce produit'}
          </DialogDescription>
        </DialogHeader>

        {/* Product summary */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border" data-testid="attach-product-summary">
          {product.image ? (
            <img src={product.image} alt="" className="w-12 h-12 object-cover rounded" />
          ) : (
            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center"><Package className="h-5 w-5 text-gray-400" /></div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{product.intitule}</p>
            <p className="text-xs text-gray-500">EAN: {product.code_ean}{product.editeur ? ` — ${product.editeur}` : ''}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          {/* Pricing + Identification */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1.5 text-sm font-medium mb-1.5">
                <DollarSign className="h-3.5 w-3.5 text-gray-400" /> Prix d&apos;achat HT
              </Label>
              <Input type="number" step="0.01" min="0" value={form.purchase_price}
                onChange={(e) => set('purchase_price', e.target.value)}
                placeholder="0.00" className="placeholder:text-gray-300 placeholder:italic placeholder:opacity-60"
                data-testid="dist-purchase-price" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5 text-sm font-medium mb-1.5">
                <Hash className="h-3.5 w-3.5 text-gray-400" /> Réf. distributeur
              </Label>
              <Input value={form.supplier_reference} onChange={(e) => set('supplier_reference', e.target.value)}
                placeholder="REF-12345" className="placeholder:text-gray-300 placeholder:italic placeholder:opacity-60"
                data-testid="dist-supplier-ref" />
            </div>
          </div>

          {/* Availability */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1.5 text-sm font-medium mb-1.5"><Box className="h-3.5 w-3.5 text-gray-400" /> Disponibilité</Label>
              <Select value={form.availability_status} onValueChange={(v) => set('availability_status', v)}>
                <SelectTrigger data-testid="dist-availability"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">En stock</SelectItem>
                  <SelectItem value="unavailable">Indisponible</SelectItem>
                  <SelectItem value="unknown">Non communiqué</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1.5 text-sm font-medium mb-1.5"><Calendar className="h-3.5 w-3.5 text-gray-400" /> Date retour stock</Label>
              <Input type="date" value={form.date_disponibilite_iso} onChange={(e) => set('date_disponibilite_iso', e.target.value)} data-testid="dist-date-return" />
            </div>
          </div>

          {/* Comment */}
          <div>
            <Label className="flex items-center gap-1.5 text-sm font-medium mb-1.5"><MessageSquare className="h-3.5 w-3.5 text-gray-400" /> Commentaire</Label>
            <Textarea value={form.commentaire_disponibilite} onChange={(e) => set('commentaire_disponibilite', e.target.value)}
              placeholder="Réappro prévue..." rows={2} className="placeholder:text-gray-300 placeholder:italic placeholder:opacity-60" data-testid="dist-comment" />
          </div>

          {/* ═══ CONDITIONS D'ACHAT ═══ */}
          <div className="border-t pt-4">
            <OfferSection
              ref={offerRef}
              mechanics={form.promotional_mechanics}
              onChange={(newMechs) => set('promotional_mechanics', newMechs)}
              userRole="partenaire"
            />
          </div>

          {/* Logistics */}
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-3"><Truck className="h-3.5 w-3.5 text-gray-400" /> Logistique</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[['minimum_commande', 'Min. cmd', 'dist-min-order'], ['maximum_commande', 'Max. cmd', 'dist-max-order'],
                ['multiple_commande', 'Multiple', 'dist-multiple'], ['colisage', 'Colisage', 'dist-colisage']].map(([f, l, tid]) => (
                <div key={f}>
                  <Label className="text-xs text-gray-500 mb-1 block">{l}</Label>
                  <Input type="number" min="0" value={form[f]} onChange={(e) => set(f, e.target.value)}
                    placeholder="\u2014" className="placeholder:text-gray-300 placeholder:italic placeholder:opacity-60" data-testid={tid} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Dimension carton</Label>
                <Input value={form.dimension_carton} onChange={(e) => set('dimension_carton', e.target.value)}
                  placeholder="30x20x10 cm" className="placeholder:text-gray-300 placeholder:italic placeholder:opacity-60" data-testid="dist-dimension-carton" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Cartons/palette</Label>
                <Input type="number" min="1" value={form.nombre_carton_par_palette}
                  onChange={(e) => set('nombre_carton_par_palette', e.target.value)}
                  placeholder="48" className="placeholder:text-gray-300 placeholder:italic placeholder:opacity-60" data-testid="dist-cartons-palette" />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t sticky bottom-0 bg-white pb-1">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} data-testid="dist-cancel-btn">Annuler</Button>
            <Button type="submit" disabled={submitting} data-testid="dist-submit-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {submitting ? (editMode ? 'Enregistrement...' : 'Création...') : (editMode ? 'Enregistrer' : 'Créer ma distribution')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DistributionAttachForm;
